import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { FlowNode, ConditionData } from '../../types';
import { Icon } from '../Icon';
import { NodeIssueBadge } from './NodeIssueBadge';

const OP_LABEL: Record<string, string> = {
  equals: 'is',
  not_equals: 'is not',
  gt: 'more than',
  lt: 'less than',
  gte: 'at least',
  lte: 'at most',
  contains: 'contains',
  starts_with: 'starts with',
};

function ConditionNodeImpl({ data, selected, id }: NodeProps<FlowNode>) {
  const d = data as ConditionData;
  const isCustomer = d.field.startsWith('customer.');
  const isInvoice = d.field.startsWith('invoice.');
  const isStorm = d.field.startsWith('storm.');

  return (
    <div className={`vnode cond ${selected ? 'selected' : ''}`} data-node-id={id}>
      <NodeIssueBadge issues={(d as any).__issues} />
      <Handle type="target" position={Position.Top} id="in" />
      <div className="vnode-head">
        <span className="vnode-lbl">
          <Icon name="diamond" size={11} variant="amber" />
          Condition · only if
        </span>
      </div>

      {isCustomer && <CustomerCheckPreview d={d} />}
      {isInvoice && <InvoiceCheckPreview d={d} />}
      {isStorm && <StormCheckPreview d={d} />}
      {!isCustomer && !isInvoice && !isStorm && <GenericCheckPreview d={d} />}

      <div className="vcard-branches">
        <span className="branch-pill match"><Icon name="check" size={10} variant="green" /> match · go</span>
        <span className="branch-pill nomatch"><Icon name="x" size={10} variant="muted" /> no match · skip</span>
      </div>
      <Handle type="source" position={Position.Bottom} id="match" style={{ left: '30%' }} />
      <Handle type="source" position={Position.Bottom} id="nomatch" style={{ left: '70%' }} />
    </div>
  );
}

export const ConditionNode = memo(ConditionNodeImpl);

function CustomerCheckPreview({ d }: { d: ConditionData }) {
  return (
    <div className="vcard vcard-customer">
      <div className="vcard-customer-row">
        <div className="vcard-av">JS</div>
        <div className="vcard-customer-info">
          <b>John Smith</b>
          <span>📍 Dallas, TX · sample</span>
        </div>
      </div>
      <div className="vcard-checklist">
        <div className="vcard-check-row">
          <span className="vcard-check ✓">✓</span>
          <code className="vcard-mtag">{d.field}</code>
          <span>{OP_LABEL[d.operator] || d.operator}</span>
          <code className="vcard-mtag">{d.value}</code>
        </div>
        {d.also?.map((c, i) => (
          <div className="vcard-check-row" key={i}>
            <span className="vcard-check">✓</span>
            <code className="vcard-mtag">{c.field}</code>
            <span>{OP_LABEL[c.operator] || c.operator}</span>
            <code className="vcard-mtag">{c.value}</code>
          </div>
        ))}
      </div>
      <span className="vcard-meta">{d.label}</span>
    </div>
  );
}

function InvoiceCheckPreview({ d }: { d: ConditionData }) {
  return (
    <div className="vcard vcard-invoice-check">
      <div className="vcard-invoice-head">
        <span>INVOICE FILTER</span>
        <b>{d.label}</b>
      </div>
      <div className="vcard-check-row">
        <span className="vcard-check">✓</span>
        <code className="vcard-mtag">{d.field}</code>
        <span>{OP_LABEL[d.operator] || d.operator}</span>
        <code className="vcard-mtag">{d.value}</code>
      </div>
    </div>
  );
}

function StormCheckPreview({ d }: { d: ConditionData }) {
  return (
    <div className="vcard vcard-storm-check">
      <div className="vcard-storm-row">
        <span>⛈</span>
        <b>{d.label}</b>
      </div>
      <div className="vcard-check-row">
        <span className="vcard-check">✓</span>
        <code className="vcard-mtag">{d.field}</code>
        <span>{OP_LABEL[d.operator] || d.operator}</span>
        <code className="vcard-mtag">{d.value}</code>
      </div>
    </div>
  );
}

function GenericCheckPreview({ d }: { d: ConditionData }) {
  return (
    <div className="vcard vcard-generic-check">
      <b>{d.label}</b>
      <div className="vcard-check-row">
        <span className="vcard-check">✓</span>
        <code className="vcard-mtag">{d.field}</code>
        <span>{OP_LABEL[d.operator] || d.operator}</span>
        <code className="vcard-mtag">{d.value}</code>
      </div>
    </div>
  );
}
